import React, { useState, useMemo } from 'react';
import { useFamilyContext } from '../../contexts/FamilyContext';
import { useNavigate, Link } from 'react-router-dom';
import {
  format,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  eachDayOfInterval,
  isSameMonth,
  isSameDay,
  addMonths,
  subMonths,
  compareAsc,
  parseISO,
  addDays
} from 'date-fns';
import {
  ArrowLeft,
  ChevronLeft,
  ChevronRight,
  Calendar,
  Clock,
  MapPin,
  Users,
  Filter,
  X,
  Plus,
  Edit3,
  Lightbulb, Heart,
  Trash2,
  Download,
  Plane
} from 'lucide-react';

// Components
import SmartAddModal from './SmartAddModal';
import EventForm from './EventForm';
import CalendarEventEditor from './CalendarEventEditor';
import CalendarImportModal from './CalendarImportModal';
import AvatarOrInitial from './AvatarOrInitial';

// Styles
import '../../styles/simplified-calendar.css';

// Simplified filter options
const CALENDAR_FILTERS = [
  { key: 'family', label: 'Family Activities', color: 'bg-amber-500' },
  { key: 'scheduled-events', label: 'Scheduled Events', color: 'bg-coral-500' },
  { key: 'personal-events', label: 'Personal Events', color: 'bg-sky-500' },
  { key: 'school', label: 'School District', color: 'bg-indigo-600' },
  { key: 'sports', label: 'Sports', color: 'bg-emerald-500' },
  { key: 'community', label: 'Community Events', color: 'bg-violet-500' },
  { key: 'external', label: 'Other External', color: 'bg-slate-500' },
  { key: 'other', label: 'Other', color: 'bg-teal-500' }
];

export default function SimplifiedCalendar() {
  const navigate = useNavigate();
  const { 
    familyActivities = [], 
    familyCalendar = [], 
    currentUser,
    removeCalendarEvent,
    cancelFamilyActivity
  } = useFamilyContext();

  // Helper function to create a Date object from YYYY-MM-DD string in local timezone
  const localDateFromString = (dateString) => {
    if (!dateString) return null;
    const [year, month, day] = dateString.split('-').map(Number);
    return new Date(year, month - 1, day);
  };
  
  const [currentDate, setCurrentDate] = useState(new Date());
  const [activeFilters, setActiveFilters] = useState(CALENDAR_FILTERS.map(f => f.key));
  const [showFilters, setShowFilters] = useState(false);

  const [selectedEvent, setSelectedEvent] = useState(null);
  const [selectedDayEvents, setSelectedDayEvents] = useState(null);
  const [showAddEventForm, setShowAddEventForm] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  // Handle delete event
  const handleDeleteEvent = async (event) => {
    const confirmMessage = `Are you sure you want to delete "${event.title}"?`;
    
    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      if (event.type === 'family-activity' && event.source) {
        await cancelFamilyActivity(event.source.id);
      } else if (event.source) {
        await removeCalendarEvent(event.source.id);
      }
      
      // Close modals after successful deletion
      setSelectedEvent(null);
      setSelectedDayEvents(null);
      
      // Force a refresh of the calendar
      setRefreshKey(prev => prev + 1);
    } catch (error) {
      console.error('Error deleting event:', error);
      alert('Failed to delete event. Please try again.');
    }
  };

  // Handle calendar import complete
  const handleImportComplete = (results) => {
    console.log('Calendar import completed:', results);
    setShowImportModal(false);
    setRefreshKey(prev => prev + 1);
  };

  // Process data for badge counts
  const processedData = useMemo(() => {
    if (!familyActivities || !currentUser) {
      return {
        activeVotes: [],
        pendingRSVPs: []
      };
    }

    const activeVotes = familyActivities.filter(activity => {
      if (activity.confirmed_for_calendar) return false;
      if (activity.status === 'archived') return false;
      const ratings = activity.ratings || {};
      const hasRatings = Object.keys(ratings).length > 0;
      const currentUserHasVoted = ratings[currentUser.id] !== undefined;
      return hasRatings || !currentUserHasVoted;
    });

    const pendingRSVPs = familyActivities.filter(activity => {
      if (!activity.confirmed_for_calendar) return false;
      if (activity.start_date) {
        const eventDate = new Date(activity.start_date);
        const now = new Date();
        if (eventDate < now) return false;
      }
      const rsvps = activity.rsvps || {};
      return rsvps[currentUser.id] === undefined;
    });

    return {
      activeVotes,
      pendingRSVPs
    };
  }, [familyActivities, currentUser]);

  // Process and combine all events
  const allEvents = useMemo(() => {
    const events = [];

    // Family Activities (both confirmed and unconfirmed, excluding archived)
    if (activeFilters.includes('family')) {
      familyActivities.forEach(activity => {
        if (activity.status === 'archived') return;
        const activityDate = activity.start_date || activity.date;
        if (activityDate) {
          events.push({
            id: `activity-${activity.id}`,
            title: activity.title,
            date: activityDate,
            endDate: activity.end_date,
            time: activity.time,
            location: activity.location,
            description: activity.description,
            type: 'family-activity',
            category: 'family',
            color: 'bg-gold-500',
            isConfirmed: activity.confirmed_for_calendar,
            source: activity,
            rsvps: activity.rsvps || {},
            status: activity.status
          });
        }
      });
    }

    // Calendar Events (scheduled events) - exclude external events and school events
    if (activeFilters.includes('scheduled-events')) {
      familyCalendar.forEach(event => {
        if (event.date && !event.personal_event && !event.external_source && event.type !== 'family-time' && event.type !== 'school') {
          events.push({
            id: `calendar-${event.id}`,
            title: event.title,
            date: event.date,
            endDate: event.end_date,
            time: event.time,
            location: event.location,
            description: event.description,
            type: 'calendar-event',
            category: 'scheduled-events',
            color: 'bg-coral-500',
            source: event
          });
        }
      });
    }

    // Personal Events
    if (activeFilters.includes('personal-events')) {
      familyCalendar.forEach(event => {
        if (event.date && (event.personal_event || event.created_by === currentUser?.id)) {
          events.push({
            id: `personal-${event.id}`,
            title: event.title,
            date: event.date,
            endDate: event.end_date,
            time: event.time,
            location: event.location,
            description: event.description,
            type: 'personal-event',
            category: 'personal-events',
            color: 'bg-azure-500',
            source: event
          });
        }
      });
    }

    // External Calendar Events (school, sports, community, etc.)
    ['school', 'sports', 'community', 'external'].forEach(externalCategory => {
      if (activeFilters.includes(externalCategory)) {
        familyCalendar.forEach(event => {
          // Check if this is an external event with matching category
          // For school events, also include events with type='school' even without external_source
          const isSchoolEvent = externalCategory === 'school' && (
            event.type === 'school' || 
            event.category === 'school' ||
            (event.external_source && (event.external_source === 'ical_url' || event.external_source === 'ical_file' || event.external_source === 'school_district'))
          );
          
          const isOtherExternalEvent = externalCategory !== 'school' && event.external_source && 
              (event.type === externalCategory || event.category === externalCategory);
          
          if (event.date && (isSchoolEvent || isOtherExternalEvent)) {
            const filterConfig = CALENDAR_FILTERS.find(f => f.key === externalCategory);
            events.push({
              id: `external-${event.id}`,
              title: event.title,
              date: event.date,
              endDate: event.end_date,
              time: event.time,
              location: event.location,
              description: event.description,
              type: 'external-event',
              category: externalCategory,
              color: filterConfig?.color || 'bg-stone-500',
              source: event,
              externalSource: event.external_source,
              externalCalendarId: event.external_calendar_id,
              badge: event.external_calendar_id || event.external_source
            });
          }
        });
      }
    });

    // Other Events (family-time, reminders, etc.) - exclude external events
    if (activeFilters.includes('other')) {
      familyCalendar.forEach(event => {
        if (event.date && !event.external_source && !event.personal_event && (event.type === 'family-time' || event.category === 'reminder' || event.category === 'holiday' || (!event.type && !event.category))) {
          events.push({
            id: `other-${event.id}`,
            title: event.title,
            date: event.date,
            endDate: event.end_date,
            time: event.time,
            location: event.location,
            description: event.description,
            type: event.type || 'other',
            category: 'other',
            color: 'bg-teal-500',
            source: event
          });
        }
      });
    }

    return events.sort((a, b) => {
      // Sort by date, then by time
      const dateComparison = compareAsc(parseISO(a.date), parseISO(b.date));
      if (dateComparison !== 0) return dateComparison;
      
      // If same date, sort by time
      if (a.time && b.time) {
        return a.time.localeCompare(b.time);
      }
      if (a.time && !b.time) return -1;
      if (!a.time && b.time) return 1;
      return 0;
    });
  }, [familyActivities, familyCalendar, activeFilters, currentUser, refreshKey]);

  // Get calendar grid days
  const calendarDays = useMemo(() => {
    const monthStart = startOfMonth(currentDate);
    const monthEnd = endOfMonth(currentDate);
    const calendarStart = startOfWeek(monthStart, { weekStartsOn: 0 });
    const calendarEnd = endOfWeek(monthEnd, { weekStartsOn: 0 });
    return eachDayOfInterval({ start: calendarStart, end: calendarEnd });
  }, [currentDate]);

  // Get events for a specific day
  const getEventsForDay = (date) => {
    return allEvents.filter(event => {
      try {
        const eventDate = parseISO(event.date);
        if (event.endDate && event.endDate !== event.date) {
          const endDate = parseISO(event.endDate);
          return date >= eventDate && date <= endDate;
        } else {
          return isSameDay(eventDate, date);
        }
      } catch {
        return false;
      }
    });
  };

  // Get upcoming events for the list (next 90 days)
  const upcomingEvents = useMemo(() => {
    const now = new Date();
    const ninetyDaysFromNow = addDays(now, 90);
    
    return allEvents.filter(event => {
      try {
        const eventDate = parseISO(event.date);
        return eventDate >= now && eventDate <= ninetyDaysFromNow;
      } catch {
        return false;
      }
    }).slice(0, 10); // Limit to 10 events
  }, [allEvents]);

  const toggleFilter = (filterKey) => {
    setActiveFilters(prev => 
      prev.includes(filterKey) 
        ? prev.filter(f => f !== filterKey)
        : [...prev, filterKey]
    );
  };

  // Get date display for event list
  const getEventDateDisplay = (dateString) => {
    try {
      const date = parseISO(dateString);
      const today = new Date();
      
      if (isSameDay(date, today)) return 'Today';
      if (isSameDay(date, addMonths(today, 0))) {
        const tomorrow = new Date(today);
        tomorrow.setDate(today.getDate() + 1);
        if (isSameDay(date, tomorrow)) return 'Tomorrow';
      }
      
      return format(date, 'EEE, MMM d');
    } catch {
      return dateString;
    }
  };

  return (
    <div className="simplified-calendar">
      {/* Header */}
      <div className="bg-white border-b border-stone-200 sticky top-0 z-50">
        <div className="px-4 py-3">
          {/* Top row: Back button, Section name with color bar, Add button */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate(-1)}
                className="p-2 text-stone-600 hover:bg-stone-100 rounded-lg transition-colors"
                style={{ minWidth: '44px', minHeight: '44px' }}
                aria-label="Go back"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              
              <div className="flex items-center gap-3">
                {/* Color bar */}
                <div className="w-2 h-6 bg-gold-500 rounded-full"></div>
                <div>
                  <h1 className="text-xl font-bold text-slate-900 tracking-tight">Family Events</h1>
                </div>
              </div>
            </div>

            {/* Action buttons */}
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowImportModal(true)}
                className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-azure-600 bg-azure-50 hover:bg-azure-100 rounded-lg transition-colors"
                title="Import external calendar"
              >
                <Download className="w-4 h-4" />
                <span className="hidden sm:inline">Import Calendar</span>
              </button>
              <button
                onClick={() => setShowAddEventForm(true)}
                className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-white bg-gold-500 hover:bg-gold-600 rounded-lg transition-colors"
                title="Add new event"
              >
                <Plus className="w-4 h-4" />
                <span className="hidden sm:inline">Add Event</span>
              </button>
            </div>
          </div>
        </div>
        
        {/* Bottom row: Tab Navigation */}
        <div className="px-0">
          <div className="flex">
          <Link 
              to="/family" 
              className="flex-1 px-4 py-4 text-xs sm:text-sm font-medium transition-all duration-150 border-b-2 border-transparent text-stone-600 hover:text-slate-900 hover:bg-stone-50"
              style={{ minHeight: '48px' }}
            >
              <div className="flex items-center justify-center gap-2">
                <Lightbulb className="w-4 h-4" />
                <span>Things to Do</span>
                {(processedData.activeVotes.length > 0 || processedData.pendingRSVPs.length > 0) && (
                  <div className="bg-coral-500 text-white text-xs w-5 h-5 rounded-full flex items-center justify-center font-medium">
                    {processedData.activeVotes.length + processedData.pendingRSVPs.length}
                  </div>
                )}
              </div>
            </Link>
            <Link 
              to="/calendar" 
              className="flex-1 px-4 py-4 text-xs sm:text-sm font-medium transition-all duration-150 border-b-2 border-gold-500 text-slate-900 bg-gold-50"
              style={{ minHeight: '48px' }}
            >
              <div className="flex items-center justify-center gap-2">
                <Calendar className="w-4 h-4" />
                <span>Calendar</span>
              </div>
            </Link>
            <Link 
              to="/family?tab=trips" 
              className="flex-1 px-2 sm:px-4 py-4 text-xs sm:text-sm font-medium transition-all duration-150 border-b-2 border-transparent text-stone-600 hover:text-slate-900 hover:bg-stone-50"
              style={{ minHeight: '48px' }}
            >
              <div className="flex items-center justify-center gap-1 sm:gap-2">
                <Plane className="w-4 h-4 flex-shrink-0" />
                <span className="truncate">Trips</span>
              </div>
            </Link>
            <Link 
              to="/family?tab=invites" 
              className="flex-1 px-2 sm:px-4 py-4 text-xs sm:text-sm font-medium transition-all duration-150 border-b-2 border-transparent text-stone-600 hover:text-slate-900 hover:bg-stone-50"
              style={{ minHeight: '48px' }}
            >
              <div className="flex items-center justify-center gap-1 sm:gap-2">
                <Heart className="w-4 h-4 flex-shrink-0" />
                <span className="truncate">1:1</span>

              </div>
            </Link>

          </div>
        </div>
      </div>

            {/* Calendar Content */}
      <div className="px-4 py-4 pb-20">
        {/* Calendar Navigation */}
        <div className="calendar-nav mb-4">
          <div className="calendar-nav-controls">
            <button onClick={() => setCurrentDate(subMonths(currentDate, 1))} className="calendar-nav-btn">
              <ChevronLeft className="w-5 h-5" />
            </button>
            <h2 className="calendar-period-title">
              {format(currentDate, 'MMMM yyyy')}
            </h2>
            <button onClick={() => setCurrentDate(addMonths(currentDate, 1))} className="calendar-nav-btn">
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                showFilters 
                  ? 'bg-stone-100 text-stone-700' 
                  : 'text-stone-600 hover:bg-stone-100 hover:text-stone-700'
              }`}
              title="Filter Events"
            >
              <Filter className="w-4 h-4" />
              <span className="hidden sm:inline">Filters</span>
              {activeFilters.length < CALENDAR_FILTERS.length && (
                <span className="bg-coral-500 text-white text-xs w-5 h-5 rounded-full flex items-center justify-center font-medium">
                  {activeFilters.length}
                </span>
              )}
            </button>
          </div>
        </div>

        {/* Enhanced Filters */}
        {showFilters && (
          <div className="bg-stone-50 border border-stone-200 rounded-xl p-4 mb-6">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-stone-900">Filter Calendar Events</h3>
              <div className="flex gap-2">
                <button
                  onClick={() => setActiveFilters(CALENDAR_FILTERS.map(f => f.key))}
                  className="text-xs text-azure-600 hover:text-azure-800 font-medium"
                >
                  Show All
                </button>
                <button
                  onClick={() => setActiveFilters([])}
                  className="text-xs text-stone-500 hover:text-stone-700 font-medium"
                >
                  Hide All
                </button>
              </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
              {CALENDAR_FILTERS.map(filter => {
                const isActive = activeFilters.includes(filter.key);
                return (
                  <button
                    key={filter.key}
                    onClick={() => toggleFilter(filter.key)}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                      isActive 
                        ? 'bg-white border-2 border-stone-300 text-stone-900 shadow-sm' 
                        : 'bg-transparent border-2 border-transparent text-stone-500 hover:text-stone-700 hover:bg-white hover:border-stone-200'
                    }`}
                  >
                    <div className={`w-3 h-3 rounded-full ${filter.color} ${isActive ? 'shadow-sm' : 'opacity-60'}`} />
                    <span className="flex-1 text-left">{filter.label}</span>
                    {isActive && <div className="w-2 h-2 bg-emerald-500 rounded-full" />}
                  </button>
                );
              })}
            </div>
            <div className="flex items-center justify-between mt-3 pt-3 border-t border-stone-200">
              <p className="text-xs text-stone-500">
                Showing {activeFilters.length} of {CALENDAR_FILTERS.length} categories
              </p>
              <button
                onClick={() => setShowFilters(false)}
                className="text-xs text-stone-500 hover:text-stone-700 font-medium"
              >
                Close Filters
              </button>
            </div>
          </div>
        )}

        {/* Quick Filter Bar - Always visible when filters are applied */}
        {activeFilters.length > 0 && activeFilters.length < CALENDAR_FILTERS.length && !showFilters && (
          <div className="flex items-center gap-2 mb-4 p-3 bg-azure-50 border border-azure-200 rounded-lg">
            <span className="text-sm font-medium text-azure-900">Active filters:</span>
            <div className="flex flex-wrap gap-1">
              {activeFilters.map(filterKey => {
                const filter = CALENDAR_FILTERS.find(f => f.key === filterKey);
                return (
                  <button
                    key={filterKey}
                    onClick={() => toggleFilter(filterKey)}
                    className="flex items-center gap-1 px-2 py-1 bg-white border border-azure-300 rounded-md text-xs font-medium text-azure-800 hover:bg-azure-100 transition-colors"
                  >
                    <div className={`w-2 h-2 rounded-full ${filter.color}`} />
                    {filter.label}
                    <X className="w-3 h-3 ml-1" />
                  </button>
                );
              })}
            </div>
            <button
              onClick={() => setActiveFilters(CALENDAR_FILTERS.map(f => f.key))}
              className="text-xs text-azure-600 hover:text-azure-800 font-medium ml-auto"
            >
              Clear all
            </button>
          </div>
        )}

        {/* Desktop Two-Column Layout / Mobile Stacked Layout */}
        <div className="flex flex-col lg:flex-row gap-6">
          {/* Calendar Grid */}
          <div className="flex-1 lg:max-w-2xl">
            <div className="calendar-grid-section">
              <div className="calendar-weekdays">
                {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                  <div key={day} className="calendar-weekday">{day}</div>
                ))}
              </div>
              
              <div className="calendar-grid">
                {calendarDays.map((date, idx) => {
                  const dayEvents = getEventsForDay(date);
                  const isCurrentMonth = isSameMonth(date, currentDate);
                  const isCurrentDay = isSameDay(date, new Date());
                  
                  return (
                    <div
                      key={idx}
                      className={`calendar-day ${
                        isCurrentMonth ? 'current-month' : 'other-month'
                      } ${isCurrentDay ? 'today' : ''} ${dayEvents.length > 0 ? 'has-events cursor-pointer' : ''}`}
                      onClick={() => {
                        if (dayEvents.length === 1) {
                          // Single event - show it directly
                          setSelectedEvent(dayEvents[0]);
                        } else if (dayEvents.length > 1) {
                          // Multiple events - show selection modal
                          setSelectedDayEvents(dayEvents);
                        }
                      }}
                    >
                      <div className="calendar-day-number">
                        {format(date, 'd')}
                      </div>
                      {dayEvents.length > 0 && (
                                                <div className="calendar-day-events">
                          {dayEvents.slice(0, 3).map((event) => (
                            <div
                              key={event.id}
                              className={`calendar-event-dot ${event.category}`}
                              title={`${event.title}${event.time ? ` at ${event.time}` : ''}`}
                            />
                          ))}
                          {dayEvents.length > 3 && (
                            <div 
                              className="calendar-more-events"
                              title={`${dayEvents.length - 3} more events`}
                            >
                              +{dayEvents.length - 3}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Events Sidebar */}
          <div className="w-full lg:w-80 xl:w-96">
            <div className="upcoming-events-section">
              <div className="section-header">
                <h2 className="section-title">Upcoming Events</h2>
                <span className="event-count">{upcomingEvents.length} events</span>
              </div>
              
              {upcomingEvents.length === 0 ? (
                <div className="no-events">
                  <Calendar className="w-12 h-12 text-stone-400 mx-auto mb-3" />
                  <p className="text-stone-500">No upcoming events</p>
                </div>
              ) : (
                <div className="events-list max-h-96 lg:max-h-[calc(100vh-300px)] overflow-y-auto">
                  {upcomingEvents.map(event => (
                    <div key={event.id} className="event-item" onClick={() => setSelectedEvent(event)}>
                      <div className="event-item-main">
                        <div className="event-item-content">
                          <div className="event-item-header">
                            <div className={`event-type-indicator ${event.color}`} />
                            <div className="event-title">{event.title}</div>
                            {!event.isConfirmed && event.type === 'family-activity' && (
                              <div className="event-status-badge unconfirmed">
                                Needs Vote
                              </div>
                            )}
                            {event.isConfirmed && event.type === 'family-activity' && (
                              <div className="event-status-badge confirmed">
                                Confirmed
                              </div>
                            )}
                          </div>
                          
                          <div className="event-details">
                            <div className="event-date-time">
                              <Calendar className="w-4 h-4" />
                              <span>{getEventDateDisplay(event.date)}</span>
                              {event.time && (
                                <>
                                  <Clock className="w-4 h-4 ml-2" />
                                  <span>{event.time}</span>
                                </>
                              )}
                            </div>
                            
                            {event.location && (
                              <div className="event-location">
                                <MapPin className="w-4 h-4" />
                                <span>{event.location}</span>
                              </div>
                            )}
                            
                            {event.created_by && (
                              <div className="event-creator">
                                {(() => {
                                  const allMembers = [...(family?.kids || []), ...(family?.parents || [])];
                                  const creator = allMembers.find(m => m.id === event.created_by);
                                  if (creator) {
                                    return (
                                      <div className="flex items-center gap-2 text-sm text-stone-500">
                                        <AvatarOrInitial
                                          avatar={creator.avatar}
                                          name={creator.name}
                                          size="xs"
                                        />
                                        <span>Added by {creator.name}</span>
                                      </div>
                                    );
                                  }
                                  return null;
                                })()}
                              </div>
                            )}
                            
                            {event.description && (
                              <div className="event-description">
                                {event.description}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Modals */}
      {showAddEventForm && (
        <SmartAddModal
          isOpen={showAddEventForm}
          onClose={() => {
            setShowAddEventForm(false);
            setRefreshKey(prev => prev + 1);
          }}
        />
      )}

      {/* Calendar Import Modal */}
      <CalendarImportModal
        isOpen={showImportModal}
        onClose={() => setShowImportModal(false)}
        onImportComplete={handleImportComplete}
      />

      {/* Event Selection Modal for Multiple Events */}
      {selectedDayEvents && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full max-h-[80vh] overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-stone-200">
              <h2 className="text-lg font-semibold text-slate-900">
                {format(localDateFromString(selectedDayEvents[0].date) || new Date(selectedDayEvents[0].date), 'MMMM d, yyyy')} Events
              </h2>
              <button
                onClick={() => setSelectedDayEvents(null)}
                className="p-2 text-stone-400 hover:text-stone-600 hover:bg-stone-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Event List */}
            <div className="p-4 overflow-y-auto max-h-96">
              <div className="space-y-3">
                {selectedDayEvents.map(event => (
                  <button
                    key={event.id}
                    onClick={() => {
                      setSelectedEvent(event);
                      setSelectedDayEvents(null);
                    }}
                    className="w-full text-left p-3 rounded-lg border border-stone-200 hover:bg-stone-50 transition-colors"
                  >
                    <div className="flex items-start gap-3">
                      <div className={`w-3 h-3 rounded-full mt-1 ${event.color}`} />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <div className="font-medium text-slate-900 flex-1">{event.title}</div>
                          {event.badge && (
                            <span className="px-2 py-0.5 text-xs font-medium bg-azure-100 text-azure-800 rounded-full">
                              {event.badge}
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-2 mt-1">
                          {event.time && (
                            <div className="flex items-center gap-1 text-xs text-stone-600">
                              <Clock className="w-3 h-3" />
                              <span>{event.time}</span>
                            </div>
                          )}
                          {event.location && (
                            <div className="flex items-center gap-1 text-xs text-stone-500">
                              <MapPin className="w-3 h-3" />
                              <span className="truncate">{event.location}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Single Event Details Modal */}
      {selectedEvent && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-stone-200">
              <h2 className="text-lg font-semibold text-slate-900">Event Details</h2>
              <button
                onClick={() => setSelectedEvent(null)}
                className="p-2 text-stone-400 hover:text-stone-600 hover:bg-stone-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Content */}
            <div className="p-4">
              <div className="space-y-3">
                <div>
                  <h3 className="text-xl font-semibold text-slate-900">{selectedEvent.title}</h3>
                  <div className={`inline-block px-2 py-1 rounded-full text-xs font-medium mt-1 ${selectedEvent.color} text-white`}>
                    {selectedEvent.type === 'family-activity' ? 'Family Activity' : 
                     selectedEvent.type === 'personal-event' ? 'Personal Event' : 
                     selectedEvent.type === 'calendar-event' ? 'Scheduled Event' : 'Event'}
                  </div>
                </div>
                
                <div className="event-details space-y-2">
                  <div className="flex items-center gap-2 text-stone-600">
                    <Calendar className="w-4 h-4" />
                    <span>{format(localDateFromString(selectedEvent.date) || new Date(selectedEvent.date), 'EEEE, MMMM d, yyyy')}</span>
                  </div>
                  
                  {selectedEvent.time && (
                    <div className="flex items-center gap-2 text-stone-600">
                      <Clock className="w-4 h-4" />
                      <span>{selectedEvent.time}</span>
                    </div>
                  )}
                  
                  {selectedEvent.location && (
                    <div className="flex items-center gap-2 text-stone-600">
                      <MapPin className="w-4 h-4" />
                      <span>{selectedEvent.location}</span>
                    </div>
                  )}
                  
                  {selectedEvent.description && (
                    <div className="mt-3 p-3 bg-stone-50 rounded-lg">
                      <p className="text-sm text-stone-700">{selectedEvent.description}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="p-4 border-t border-stone-200 bg-stone-50 rounded-b-xl flex gap-3">
              <button
                onClick={() => setSelectedEvent(null)}
                className="flex-1 px-4 py-2 bg-stone-200 text-stone-700 rounded-lg hover:bg-stone-300 transition-colors"
              >
                Close
              </button>
              {selectedEvent.source && (
                <>
                  <button
                    onClick={() => {
                      setEditingEvent(selectedEvent);
                      setSelectedEvent(null);
                    }}
                    className="flex-1 px-4 py-2 bg-slate-900 text-white rounded-lg hover:bg-slate-800 transition-colors flex items-center justify-center gap-2"
                  >
                    <Edit3 className="w-4 h-4" />
                    Edit
                  </button>
                  <button
                    onClick={() => handleDeleteEvent(selectedEvent)}
                    className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center justify-center gap-2"
                  >
                    <Trash2 className="w-4 h-4" />
                    Delete
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {editingEvent && (
        <EventForm
          event={editingEvent}
          onClose={() => {
            setEditingEvent(null);
            setRefreshKey(prev => prev + 1);
          }}
          mode="edit"
        />
      )}
    </div>
  );
}